import {BasicViewColors} from '@mintdata/mint-kit/impl/components/colorPicker/BasicViewColors';
import {HuePointer} from '@mintdata/mint-kit/impl/components/colorPicker/HuePointer';
import {Swatches} from '@mintdata/mint-kit/impl/components/colorPicker/Swatches';

import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {IPopupProps, PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {ImgIcon} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcon';
import {ImgIcons} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcons';
import {InputLabel} from '@mintdata/mint-kit/impl/properties/components/inputLabel/InputLabel';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';

import * as classnames from 'classnames/bind';
import get from 'lodash/get';
import * as React from 'react';
import {Hue, Saturation} from 'react-color/lib/components/common';

import reactColorHelper from 'react-color/lib/helpers/color';

const styles = require('@mintdata/mint-kit/impl/components/colorPicker/ColorPicker.less');
const cx = classnames.bind(styles);

export enum View
{
    Basic,
    Advanced,
}

export interface ISelectedHex
{
    hex:string;
    color:object;
}

export interface IColorPickerProps
{
    className?:string;
    popupProps?:Partial<IPopupProps>;

    color?:string;
    onChange?:(color:string, closePopover?:boolean) => void;

    onShow?:() => void;
    onHide?:(closedByButton?:boolean) => void;
}

export interface IColorPickerState
{
    view?:View;
    isPopoverOpen?:boolean;

    /**
     * The reason for this state is that the same hex value can be
     * converted to several hsl/hsv values needed for Saturation component.
     * Hence twitching effect of the hue slider is seen when dragging the saturation slider.
     */
    selectedHex?:ISelectedHex;

    inputHex?:string;
    isInputHexValid?:boolean;
}

export class ColorPicker extends React.Component<IColorPickerProps, IColorPickerState>
{
    private readonly _popoverId:string = IdUtil.newComponentId();
    private _currentSwatchRef;
    private _popoverContainerRef;

    constructor(props)
    {
        super(props);

        const selectedHex = this._getSelectedHexFromProps(props);

        this.state = {
            view: View.Basic,
            isPopoverOpen: false,
            selectedHex,
            inputHex: selectedHex.hex,
            isInputHexValid: this._validateHex(selectedHex.hex),
        };
    }

    componentWillReceiveProps(nextProps)
    {
        const selectedHex = this._getSelectedHexFromProps(nextProps);

        if( (selectedHex && !this.state.selectedHex) ||
            (selectedHex && selectedHex.hex !== this.state.selectedHex.hex) )
        {
            this.setState({
                selectedHex,
                inputHex: selectedHex.hex,
                isInputHexValid: this._validateHex(selectedHex.hex),
            });
        }
    }

    componentWillUpdate(nextProps)
    {
        this.componentWillReceiveProps(nextProps);
    }

    private _getSelectedHexFromProps({color}:IColorPickerProps):ISelectedHex | null
    {
        const hex = color || '#ffffff';

        return {
            hex,
            color: reactColorHelper.toState(hex),
        };
    }

    @bind
    private _setCurrentSwatchRef(ref):void
    {
        this._currentSwatchRef = ref;
    }

    @bind
    private _togglePopover(e:React.MouseEvent<HTMLElement>):void
    {
        e.stopPropagation();
        e.preventDefault();

        this.setState((prevState) =>
        {
            const isPopoverOpen = !prevState.isPopoverOpen;

            const {onHide, onShow} = this.props;

            isPopoverOpen
                ? onShow && onShow()
                : onHide && onHide(true);

            return {isPopoverOpen};
        });
    }

    @bind
    private _closePopover():void
    {
        const {selectedHex, isInputHexValid} = this.state;

        if( !isInputHexValid )
        {
            this.setState({
                inputHex: selectedHex.hex,
                isInputHexValid: true,
            });
        }

        this.setState({
            isPopoverOpen: false,
        });

        const {onHide} = this.props;
        onHide && onHide();
    }

    @bind
    private _switchView(view:View)
    {
        this.setState({view: view});

        // this.setState((prevState) =>
        // {
        // 	if( prevState.view === View.Basic )
        // 	{
        // 		return {view: View.Advanced};
        // 	}
        //
        // 	return {view: View.Basic};
        // });
    }

    @bind
    private _resetColor()
    {
        this._onHueChange(null);
        this._closePopover();
    }

    @bind
    private _onColorChange(value, closePopover:boolean = true):void
    {
        const {onChange} = this.props;

        if( value === null )
        {
            onChange && onChange(null);
            return;
        }

        const colorState = reactColorHelper.toState(value, value.h);

        const isShortValueFromInput = value.hex && value.hex.length == 4;

        const inputHex = isShortValueFromInput ? value.hex : colorState.hex;

        this.setState({
            selectedHex: {
                hex: colorState.hex,
                color: colorState,
            },
            inputHex,
            isInputHexValid: this._validateHex(inputHex),
        }, () =>
        {
            onChange && onChange(colorState.hex, closePopover);
        });
    }

    @bind
    private _onHueChange(value):void
    {
        this._onColorChange(value, false);
    }

    @bind
    private _onSaturationChange(value):void
    {
        this._onColorChange(value, false);
    }

    @bind
    private _onSwatchClick(value):void
    {
        this._onColorChange(value);
        this._closePopover();
    }

    private _renderFields()
    {
        const {view} = this.state;

        return (
            <div className={styles.fields}>

                <button
                    className={cx('btn')}
                    onClick={this._resetColor}
                >
                    <ImgIcon icon={ImgIcons.resetColor} className={cx('btnIcon')} />
                    <span className={cx('btnText')}>Reset color</span>
                </button>

                <button
                    className={cx('btn', 'togglerLeft', {isPressed: view === View.Basic})}
                    onClick={() => this._switchView(View.Basic)}
                >
                    <ImgIcon icon={ImgIcons.grid33} className={cx('btnIcon')} />
                </button>

                <button
                    className={cx('btn', 'togglerRight', {isPressed: view === View.Advanced})}
                    onClick={() => this._switchView(View.Advanced)}
                >
                    <ImgIcon icon={ImgIcons.colorize} className={cx('btnIcon')} />
                </button>

            </div>
        );
    }

    private _renderBasicView()
    {
        const selectedColor = this.state.selectedHex.hex;

        return (
            <div className={styles.basicView}>
                <Swatches
                    colors={BasicViewColors.black()}
                    onClick={this._onSwatchClick}
                    selectedColor={selectedColor}
                />
                <Swatches
                    colors={BasicViewColors.main()}
                    onClick={this._onSwatchClick}
                    selectedColor={selectedColor}
                />
                <Swatches
                    colors={BasicViewColors.rest()}
                    onClick={this._onSwatchClick}
                    selectedColor={selectedColor}
                />
            </div>
        );
    }

    private _renderAdvancedView()
    {
        const {selectedHex, inputHex, isInputHexValid} = this.state;

        const colorProps = {...selectedHex.color} as any;

        return (
            <div className={styles.advancedView}>

                <div className={styles.colors}>
                    <div className={styles.saturation}>
                        <Saturation
                            {...colorProps}
                            onChange={this._onSaturationChange}
                        />
                    </div>
                    <div className={styles.hue}>
                        <Hue
                            {...colorProps}
                            direction={'vertical'}
                            pointer={HuePointer}
                            onChange={this._onHueChange}
                        />
                    </div>
                </div>

                <div className={cx('hex')}>
                    {/*<span className={cx('title')}>HEX</span>*/}
                    <InputLabel text={'Hex'} />
                    <input
                        type="text"
                        className={cx('input', {isInvalid: !isInputHexValid})}
                        value={inputHex}
                        onChange={this._onChangeHexInput}
                    />
                </div>

            </div>
        );
    }

    @bind
    private _onChangeHexInput(event:React.FormEvent<HTMLInputElement>)
    {
        const newHexColor = (event.target as any).value.trim();

        const isInputHexValid = this._validateHex(newHexColor);

        if( isInputHexValid )
        {
            this._onColorChange({hex: newHexColor}, false);
        }
        else
        {
            this.setState({inputHex: newHexColor, isInputHexValid});
        }
    }

    private _validateHex(hexColor):boolean
    {
        return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hexColor);
    }

    @bind
    private _onKeyDown(e:React.KeyboardEvent<HTMLElement>)
    {
        const {view} = this.state;

        if( e.keyCode === 13 && view === View.Advanced )
        {
            e.stopPropagation();
            e.preventDefault();
            this._closePopover();
        }
    }

    @bind
    private _popoverContainerOnRef(element:HTMLDivElement)
    {
        if( !element ) return;

        this._popoverContainerRef = element;
        element.focus();
    }

    private _renderPopover()
    {
        const {view} = this.state;
        const {popupProps} = this.props;

        const sourceAnchor = (get(popupProps, 'sourceAnchor') || SourceAnchor.LeftBottom) as SourceAnchor;
        const popupAnchor = (get(popupProps, 'popupAnchor') || PopupAnchor.LeftTop) as PopupAnchor;

        return (
            <Popover
                id={this._popoverId}
                isModal={false}
                popupSource={this._currentSwatchRef}
                sourceAnchor={sourceAnchor}
                popupAnchor={popupAnchor}
                onDismissed={this._closePopover}
            >
                <div
                    className={styles.popoverContainer}
                    onKeyDown={this._onKeyDown}
                    tabIndex={0}
                    ref={this._popoverContainerOnRef}
                >
                    {this._renderFields()}
                    {
                        view === View.Basic ? (
                            this._renderBasicView()
                        ) : (
                            this._renderAdvancedView()
                        )
                    }
                </div>
            </Popover>
        );
    }

    public render()
    {
        const {isPopoverOpen} = this.state;

        return (
            <div
                className={cx('colorPicker')}
                onClick={this._togglePopover}
                ref={this._setCurrentSwatchRef}
            >
                {
                    isPopoverOpen &&
                    this._renderPopover()
                }
            </div>
        );
    }
}
